import React, {useMemo, useState} from 'react';
import {Alert, Box, CircularProgress, Tab, Tabs, Typography} from '@mui/material';
import {useQuery} from '@tanstack/react-query';
import {previewFsReport} from '@/services/auditService/auditFsPreviewDownloadService';

interface BsPlPreviewPanelProps {
    companyId: number;
    annualId: number;
    active: boolean;
    yearOfAssessment?: string;
}

/** Excel-aligned export row (subset used by Preview). */
interface BsPlExportRow {
    level2Name?: string | null;
    level3Name?: string | null;
    code?: string | null;
    auditedRounded?: number | null;
    lastYear?: number | null;
    yearAmounts?: Array<number | null> | null;
    yearTotal?: number | null;
    sectionHeader?: boolean;
    subtotal?: boolean;
    independentNode?: boolean;
    nameInColumnB?: boolean;
    amountBorderStyle?: string | null;
}

interface BsPlPreviewPayload {
    meta?: {
        firstYear?: boolean;
        multiYearPl?: boolean;
        plYearHeaders?: string[] | null;
        periodTo?: string | null;
        companyName?: string | null;
    };
    bsRows?: BsPlExportRow[];
    plRows?: BsPlExportRow[];
}

type SheetKey = 'B61' | 'B62';

const CHILD_INDENT = '        '; // 8 spaces - Excel B-column offset for level-3
const CURRENCY = 'HK$';
const SIDE_PAD = '250px';
/** Gap between bordered periodTo / lastPeriodTo amount boxes */
const AMT_COL_GAP = 10;
/** Keep empty bordered amount cells same height as valued ones */
const AMT_LINE_HEIGHT = '1.35em';
/** Top gap above table header inside scrollport (theme spacing 2 = 16px) */
const SCROLL_TOP_PAD = 16;
/** Approximate sticky thead row height (py 0.4 + line 1.25) */
const STICKY_HEAD_ROW = 22;
/** BS equity children rendered like Issued capital (indent, no bold, no border). */
const BS_EQ_PLAIN_CHILD_CODES = new Set(['BS-EQ-AL', 'BS-EQ-LFP']);

/**
 * BS & PL Preview: nested B6.1 / B6.2 tabs.
 * Outer sheet tabs stay fixed; only table body area scrolls.
 */
export function BsPlPreviewPanel({
    companyId,
    annualId,
    active,
    yearOfAssessment,
}: BsPlPreviewPanelProps) {
    const [sheet, setSheet] = useState<SheetKey>('B61');

    const {data, isLoading, isError, error, refetch} = useQuery({
        queryKey: ['fs-preview', 'BS_PL', companyId, annualId, yearOfAssessment ?? ''],
        queryFn: () => previewFsReport('BS_PL', companyId, annualId, yearOfAssessment),
        enabled: active && companyId > 0 && annualId > 0,
        staleTime: 60_000,
    });

    const payload = data as BsPlPreviewPayload | undefined;
    const showLastYear = !payload?.meta?.firstYear;
    const multiPl = !!payload?.meta?.multiYearPl;
    const yearHeaders = useMemo(
        () => (payload?.meta?.plYearHeaders ?? []).map((h) => formatColumnPeriodDate(h) || h || '-'),
        [payload?.meta?.plYearHeaders],
    );
    const periodToLabel = formatColumnPeriodDate(payload?.meta?.periodTo) || payload?.meta?.periodTo || '';
    const lastPeriodToLabel = resolvePriorYearEnd(payload?.meta?.periodTo);

    if (!active) return null;
    if (isLoading) {
        return (
            <Box sx={{p: 3, display: 'flex', justifyContent: 'center', height: '100%'}}>
                <CircularProgress size={28}/>
            </Box>
        );
    }
    if (isError) {
        return (
            <Alert
                severity="error"
                sx={{m: 2}}
                action={<button type="button" onClick={() => void refetch()}>Retry</button>}
            >
                {(error as Error)?.message || 'Failed to load BS & PL preview'}
            </Alert>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
            }}
        >
            <Tabs
                value={sheet}
                onChange={(_, v: SheetKey) => setSheet(v)}
                sx={{
                    flexShrink: 0,
                    borderBottom: 1,
                    borderColor: 'divider',
                    px: 1,
                    minHeight: 40,
                }}
            >
                <Tab value="B61" label="Balance Sheet (B6.1)" sx={{minHeight: 40, textTransform: 'none'}}/>
                <Tab value="B62" label="Profit & Loss (B6.2)" sx={{minHeight: 40, textTransform: 'none'}}/>
            </Tabs>
            {/* Only this region scrolls � sheet tabs stay fixed */}
            <Box sx={{flex: 1, minHeight: 0, overflow: 'auto', pb: 2}}>
                {/* Opaque curtain over top gap so body cannot peek above sticky thead */}
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 6,
                        height: SCROLL_TOP_PAD,
                        bgcolor: 'background.paper',
                    }}
                />
                <Box sx={{px: SIDE_PAD}}>
                    {sheet === 'B61' ? (
                        <SheetTable
                            rows={payload?.bsRows ?? []}
                            mode="single"
                            showLastYear={showLastYear}
                            periodToLabel={periodToLabel}
                            lastPeriodToLabel={lastPeriodToLabel}
                            stickyTop={SCROLL_TOP_PAD}
                        />
                    ) : (
                        <SheetTable
                            rows={payload?.plRows ?? []}
                            mode={multiPl ? 'multi' : 'single'}
                            showLastYear={showLastYear && !multiPl}
                            yearHeaders={yearHeaders}
                            periodToLabel={periodToLabel}
                            lastPeriodToLabel={lastPeriodToLabel}
                            stickyTop={SCROLL_TOP_PAD}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
}

function SheetTable({
    rows,
    mode,
    showLastYear,
    yearHeaders = [],
    periodToLabel,
    lastPeriodToLabel,
    stickyTop = 0,
}: {
    rows: BsPlExportRow[];
    mode: 'single' | 'multi';
    showLastYear: boolean;
    yearHeaders?: string[];
    periodToLabel?: string;
    lastPeriodToLabel?: string;
    /** Offset below sticky top curtain so thead sits under the opaque gap */
    stickyTop?: number;
}) {
    const colCount = useMemo(() => {
        if (mode === 'multi') return 1 + yearHeaders.length + 1;
        // name + periodTo [+ lastPeriodTo] [+ Change% when last year shown]
        if (showLastYear) return 4;
        return 2;
    }, [mode, yearHeaders.length, showLastYear]);

    const bodyRows = useMemo(() => buildBodyRows(rows), [rows]);

    if (rows.length === 0) {
        return <Typography color="text.secondary">No lines</Typography>;
    }

    return (
        <Box
            component="table"
            sx={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: 0,
                tableLayout: 'fixed',
                fontSize: 13,
                fontFamily: '"Times New Roman", Times, serif',
                '& th, & td': {py: 0.4, verticalAlign: 'bottom'},
                '& th': {
                    fontWeight: 700,
                    textAlign: 'right',
                    lineHeight: 1.25,
                    position: 'sticky',
                    zIndex: 5,
                    backgroundColor: 'background.paper',
                },
                // Two header rows stick under top curtain; cover scrolling body
                '& thead tr:nth-of-type(1) th': {
                    top: stickyTop,
                    boxShadow: 'none',
                },
                '& thead tr:nth-of-type(2) th': {
                    top: stickyTop + STICKY_HEAD_ROW,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                },
                '& th:first-of-type, & td:first-of-type': {textAlign: 'left', width: '52%', pr: 1},
                '& th.amt, & td.amt': {
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    width: showLastYear ? '14%' : '24%',
                    px: `${AMT_COL_GAP / 2}px`,
                },
                '& th.chg, & td.chg': {
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    width: '12%',
                    pl: 1,
                },
            }}
        >
            <thead>
            {mode === 'multi' ? (
                <>
                    <tr>
                        <th/>
                        {yearHeaders.map((h) => (
                            <th key={h} className="amt">{h}</th>
                        ))}
                        <th className="amt">Total</th>
                    </tr>
                    <tr>
                        <th/>
                        {yearHeaders.map((h) => (
                            <th key={`${h}-ccy`} className="amt">{CURRENCY}</th>
                        ))}
                        <th className="amt">{CURRENCY}</th>
                    </tr>
                </>
            ) : (
                <>
                    <tr>
                        <th/>
                        <th className="amt">{periodToLabel}</th>
                        {showLastYear && <th className="amt">{lastPeriodToLabel}</th>}
                        {showLastYear && <th className="chg"/>}
                    </tr>
                    <tr>
                        <th/>
                        <th className="amt">{CURRENCY}</th>
                        {showLastYear && <th className="amt">{CURRENCY}</th>}
                        {showLastYear && <th className="chg"/>}
                    </tr>
                </>
            )}
            </thead>
            <tbody>
            {bodyRows.map((item) => {
                if (item.kind === 'spacer') {
                    // Same cell padding / line-height as valued amount rows
                    return (
                        <tr key={item.key} aria-hidden>
                            <td style={{whiteSpace: 'pre', lineHeight: AMT_LINE_HEIGHT, minHeight: AMT_LINE_HEIGHT}}>
                                {'\u00a0'}
                            </td>
                            {mode === 'multi' ? (
                                <>
                                    {yearHeaders.map((h) => (
                                        <td key={`${item.key}-${h}`} className="amt">
                                            <AmtCell border={{}}>{'\u00a0'}</AmtCell>
                                        </td>
                                    ))}
                                    <td className="amt"><AmtCell border={{}}>{'\u00a0'}</AmtCell></td>
                                </>
                            ) : (
                                <>
                                    <td className="amt"><AmtCell border={{}}>{'\u00a0'}</AmtCell></td>
                                    {showLastYear && (
                                        <td className="amt"><AmtCell border={{}}>{'\u00a0'}</AmtCell></td>
                                    )}
                                    {showLastYear && (
                                        <td className="chg" style={{lineHeight: AMT_LINE_HEIGHT}}>{'\u00a0'}</td>
                                    )}
                                </>
                            )}
                        </tr>
                    );
                }
                const r = item.row;
                const idx = item.index;
                if (r.sectionHeader) {
                    return (
                        <tr key={`sec-${r.code ?? idx}`}>
                            <td colSpan={colCount} style={{fontWeight: 700, paddingTop: 4, whiteSpace: 'pre'}}>
                                {r.level2Name || ''}
                            </td>
                        </tr>
                    );
                }
                const label = resolveRowLabel(r);
                // BS-EQ-AL / BS-EQ-LFP: same as issued capital (level-3) � indent, no bold, no borders
                const eqLeaf = isBsEqLeafRow(r);
                const border = eqLeaf ? {} : amountBorderSx(r);
                const labelBold = !eqLeaf && !!(r.subtotal || (r.independentNode && !isLevel3Independent(r)));
                const amountBold = !eqLeaf && !!(r.subtotal || (r.independentNode && !isLevel3Independent(r)));
                return (
                    <tr key={`${r.code ?? 'r'}-${idx}`}>
                        <td style={{fontWeight: labelBold ? 700 : 400, whiteSpace: 'pre'}}>
                            {label}
                        </td>
                        {mode === 'multi' ? (
                            <>
                                {(r.yearAmounts ?? []).map((a, yi) => (
                                    <td key={yi} className="amt">
                                        <AmtCell border={border} bold={!!r.subtotal}>{fmtAmt(a)}</AmtCell>
                                    </td>
                                ))}
                                {Array.from({
                                    length: Math.max(0, yearHeaders.length - (r.yearAmounts?.length ?? 0)),
                                }).map((_, yi) => (
                                    <td key={`pad-${yi}`} className="amt">
                                        <AmtCell border={border}/>
                                    </td>
                                ))}
                                <td className="amt">
                                    <AmtCell border={border} bold={!!r.subtotal}>{fmtAmt(r.yearTotal)}</AmtCell>
                                </td>
                            </>
                        ) : (
                            <>
                                <td className="amt">
                                    <AmtCell border={border} bold={amountBold}>
                                        {fmtAmt(r.auditedRounded)}
                                    </AmtCell>
                                </td>
                                {showLastYear && (
                                    <td className="amt">
                                        <AmtCell border={border} bold={amountBold}>
                                            {fmtAmt(r.lastYear)}
                                        </AmtCell>
                                    </td>
                                )}
                                {showLastYear && (
                                    <td className="chg">
                                        <ChangePctCell current={r.auditedRounded} lastYear={r.lastYear}/>
                                    </td>
                                )}
                            </>
                        )}
                    </tr>
                );
            })}
            </tbody>
        </Box>
    );
}

/** Insert one blank row before each parent (section) after the first. */
function buildBodyRows(
    rows: BsPlExportRow[],
): Array<{kind: 'spacer'; key: string} | {kind: 'row'; row: BsPlExportRow; index: number}> {
    const out: Array<{kind: 'spacer'; key: string} | {kind: 'row'; row: BsPlExportRow; index: number}> = [];
    let seenSection = false;
    rows.forEach((row, index) => {
        if (row.sectionHeader) {
            if (seenSection) {
                out.push({kind: 'spacer', key: `sp-${row.code ?? index}`});
            }
            seenSection = true;
        }
        out.push({kind: 'row', row, index});
    });
    return out;
}

/** Inner amount box so adjacent Yoa / last Yoa borders keep a white gap (same height when empty). */
function AmtCell({
    children,
    border,
    bold,
}: {
    children?: React.ReactNode;
    border: React.CSSProperties;
    bold?: boolean;
}) {
    const hasBorder = !!(border.borderTop || border.borderBottom);
    const text = children == null || children === '' ? '\u00a0' : children;
    return (
        <Box
            component="span"
            sx={{
                display: 'block',
                textAlign: 'right',
                fontWeight: bold ? 700 : 400,
                lineHeight: AMT_LINE_HEIGHT,
                minHeight: AMT_LINE_HEIGHT,
                boxSizing: 'border-box',
                px: hasBorder ? 0.5 : 0,
                ...border,
            }}
        >
            {text}
        </Box>
    );
}

/** Retained profits / Loss for the year � treat like issued capital (level-3). */
function isBsEqLeafRow(r: BsPlExportRow): boolean {
    const code = (r.code || '').toUpperCase();
    return code === 'BS-EQ-AL' || code === 'BS-EQ-LFP';
}

/** Independent row written in Excel B column (level3 only), not A-column totals. */
function isLevel3Independent(r: BsPlExportRow): boolean {
    if (isBsEqLeafRow(r)) return true;
    return !!r.independentNode && !r.nameInColumnB
        && !!(r.level3Name || '').trim()
        && !(r.level2Name || '').trim();
}

/** Excel A/B name placement: level-3 / B-column names get 8-space indent. */
function resolveRowLabel(r: BsPlExportRow): string {
    if (isLevel3Independent(r) || r.nameInColumnB) {
        const name = (r.level3Name || r.level2Name || '').trim();
        return name ? CHILD_INDENT + name : '';
    }
    if (r.independentNode && !r.nameInColumnB) {
        return (r.level2Name || r.level3Name || '').trim();
    }
    if (r.subtotal) {
        return (r.level2Name || '').trim();
    }
    const child = (r.level3Name || '').trim();
    if (child) return CHILD_INDENT + child;
    return (r.level2Name || '').trim();
}

/** Map Excel AmountBorderStyle onto amount cells. */
function amountBorderSx(r: BsPlExportRow): React.CSSProperties {
    if (isBsEqLeafRow(r)) {
        return {};
    }
    const style = (r.amountBorderStyle || 'NONE').toUpperCase();
    switch (style) {
        case 'TOP_SINGLE_EG':
            return {borderTop: '1px solid #000'};
        case 'BOTTOM_SINGLE_EG':
            return {borderBottom: '1px solid #000'};
        case 'SECTION_SINGLE_EG':
        case 'GROSS_PROFIT_EG':
            return {borderTop: '1px solid #000', borderBottom: '1px solid #000'};
        case 'TOTAL_DOUBLE_EG':
            return {borderTop: '1px solid #000', borderBottom: '3px double #000'};
        default:
            // Only section subtotoals get fallback lines; level-3 independents (AL/LFP) stay plain
            if (r.subtotal) {
                return {borderTop: '1px solid #000', borderBottom: '1px solid #000'};
            }
            return {};
    }
}

/** Excel integer column; zero ? "-"; negative ? "(n)" accounting style. */
function fmtAmt(v: number | null | undefined): string {
    if (v == null || Number.isNaN(Number(v))) return '';
    const n = Number(v);
    if (n === 0) return '-';
    const abs = Math.abs(n).toLocaleString(undefined, {maximumFractionDigits: 0});
    return n < 0 ? `(${abs})` : abs;
}

/** Change% display + color: + green / - red. */
function ChangePctCell({
    current,
    lastYear,
}: {
    current: number | null | undefined;
    lastYear: number | null | undefined;
}) {
    const {text, color} = formatChangePct(current, lastYear);
    return (
        <Box
            component="span"
            sx={{
                display: 'block',
                textAlign: 'right',
                lineHeight: AMT_LINE_HEIGHT,
                minHeight: AMT_LINE_HEIGHT,
                color: color || 'inherit',
            }}
        >
            {text || '\u00a0'}
        </Box>
    );
}

/** Excel H column values; header intentionally blank. */
function formatChangePct(
    current: number | null | undefined,
    lastYear: number | null | undefined,
): {text: string; color?: string} {
    if (lastYear == null || Number(lastYear) === 0) return {text: 'N/A'};
    if (current == null || Number.isNaN(Number(current))) return {text: ''};
    const pct = ((Number(current) - Number(lastYear)) / Number(lastYear)) * 100;
    if (pct === 0) return {text: '0.00%'};
    const abs = Math.abs(pct).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    // Keep signed % text; color by sign
    const text = `${pct < 0 ? '-' : ''}${abs}%`;
    if (pct > 0) return {text, color: '#2e7d32'};
    if (pct < 0) return {text, color: '#d32f2f'};
    return {text};
}

/** Excel column header date: d/M/yyyy */
function formatColumnPeriodDate(periodTo?: string | null): string {
    const d = parsePeriodDate(periodTo);
    if (!d) return (periodTo || '').trim();
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
}

/** Prior year-end = periodTo minus 1 year. */
function resolvePriorYearEnd(periodTo?: string | null): string {
    const d = parsePeriodDate(periodTo);
    if (!d) return '';
    d.setFullYear(d.getFullYear() - 1);
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
}

function parsePeriodDate(raw?: string | null): Date | null {
    if (!raw?.trim()) return null;
    const s = raw.trim();
    const patterns: RegExp[] = [
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
        /^(\d{1,2})-(\d{1,2})-(\d{4})$/,
        /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
        /^(\d{4})\.(\d{1,2})\.(\d{1,2})$/,
    ];
    for (let i = 0; i < patterns.length; i++) {
        const m = patterns[i].exec(s);
        if (!m) continue;
        let y: number;
        let mo: number;
        let day: number;
        if (i >= 2) {
            y = Number(m[1]);
            mo = Number(m[2]);
            day = Number(m[3]);
        } else {
            day = Number(m[1]);
            mo = Number(m[2]);
            y = Number(m[3]);
        }
        const d = new Date(y, mo - 1, day);
        if (d.getFullYear() === y && d.getMonth() === mo - 1 && d.getDate() === day) {
            return d;
        }
    }
    return null;
}
